import type p5 from 'p5';
import type { PoseData, VisualConfig, PoseLandmark, Color } from '../../../types';
import type { MotionData } from '../../../utils/MotionTracker';
import type { IArtisticRenderer } from './IArtisticRenderer';
import { interpolateColor } from '../utils/ColorUtils';

/** A single sampled point in a ribbon's history. */
interface RibbonPoint {
  x: number;
  y: number;
  width: number;
}

/** A ribbon traced from one tracked landmark over time. */
interface Ribbon {
  landmark: keyof PoseData['landmarks'];
  motionKey: keyof MotionData;
  points: RibbonPoint[];
}

/**
 * Flowing Ribbons Visualization.
 *
 * Traces smooth, tapering ribbons that follow the body's extremities through
 * space. Each frame appends the current landmark position to a short history
 * buffer; the buffer is drawn as a filled, tapering band so motion leaves a
 * silky trailing ribbon. Ribbon width scales with limb motion intensity.
 *
 * State is per-person: each tracked person owns one renderer instance.
 */
export class FlowingRibbonsRenderer implements IArtisticRenderer {
  private ribbons: Ribbon[] | null = null;

  private readonly maxPoints = 24;
  private readonly baseWidth = 6;
  private readonly maxWidth = 34;

  private readonly trackedLandmarks: Array<{ landmark: keyof PoseData['landmarks']; motionKey: keyof MotionData }> = [
    { landmark: 'leftWrist', motionKey: 'leftForearm' },
    { landmark: 'rightWrist', motionKey: 'rightForearm' },
    { landmark: 'leftAnkle', motionKey: 'leftShin' },
    { landmark: 'rightAnkle', motionKey: 'rightShin' },
    { landmark: 'nose', motionKey: 'head' },
  ];

  render(p: p5, pose: PoseData, config: VisualConfig, motionData: MotionData): void {
    if (!pose.detected) {
      return;
    }

    if (!this.ribbons) {
      this.ribbons = this.trackedLandmarks.map((t) => ({
        landmark: t.landmark,
        motionKey: t.motionKey,
        points: [],
      }));
    }

    this.updateRibbons(pose, motionData, p);
    this.renderRibbons(p, config);
  }

  reset(): void {
    this.ribbons = null;
  }

  /**
   * Append the current position of each visible landmark to its ribbon history,
   * trimming to the maximum buffer length.
   */
  private updateRibbons(pose: PoseData, motionData: MotionData, p: p5): void {
    if (!this.ribbons) {
      return;
    }

    for (const ribbon of this.ribbons) {
      const landmark = pose.landmarks[ribbon.landmark] as PoseLandmark;
      if (!landmark || landmark.visibility < 0.5) {
        // Fade the ribbon out by dropping its oldest point when not visible
        if (ribbon.points.length > 0) {
          ribbon.points.shift();
        }
        continue;
      }

      const intensity = motionData[ribbon.motionKey]?.intensity ?? 0;
      const width = this.baseWidth + intensity * (this.maxWidth - this.baseWidth);

      ribbon.points.push({
        x: landmark.x * p.width,
        y: landmark.y * p.height,
        width,
      });

      while (ribbon.points.length > this.maxPoints) {
        ribbon.points.shift();
      }
    }
  }

  /**
   * Draw each ribbon as a tapering, fading band built from offset edges along
   * its point history.
   */
  private renderRibbons(p: p5, config: VisualConfig): void {
    if (!this.ribbons) {
      return;
    }

    const primary: Color = config.primaryColor;
    const secondary: Color = config.secondaryColor;

    p.noStroke();

    for (const ribbon of this.ribbons) {
      const pts = ribbon.points;
      if (pts.length < 3) {
        continue;
      }

      // Build a smooth tapering band by drawing quads between consecutive
      // segments, offset perpendicular to the local direction.
      for (let i = 1; i < pts.length; i++) {
        const prev = pts[i - 1];
        const curr = pts[i];
        if (!prev || !curr) {
          continue;
        }

        const headFraction = i / (pts.length - 1); // 0 = oldest, 1 = newest
        // Taper: newest end is widest, oldest tail is thin
        const wPrev = prev.width * (i - 1) / (pts.length - 1) * 0.5 + prev.width * 0.5 * headFraction;
        const wCurr = curr.width * headFraction;

        const dx = curr.x - prev.x;
        const dy = curr.y - prev.y;
        const len = Math.hypot(dx, dy) || 1;
        const nx = -dy / len;
        const ny = dx / len;

        const color = interpolateColor(secondary, primary, headFraction);
        const alpha = headFraction * 255 * config.primaryColor.a;
        p.fill(color.r, color.g, color.b, alpha);

        p.beginShape();
        p.vertex(prev.x + nx * wPrev, prev.y + ny * wPrev);
        p.vertex(curr.x + nx * wCurr, curr.y + ny * wCurr);
        p.vertex(curr.x - nx * wCurr, curr.y - ny * wCurr);
        p.vertex(prev.x - nx * wPrev, prev.y - ny * wPrev);
        p.endShape(p.CLOSE);
      }

      // Bright cap at the leading (newest) point
      const head = pts[pts.length - 1];
      if (head) {
        p.fill(primary.r, primary.g, primary.b, 255 * config.primaryColor.a);
        p.circle(head.x, head.y, head.width);
      }
    }
  }
}
